// Admin-side mirror of the storefront's auth-page appearance: sign-in, sign-up, the token landing
// page and the poll confirm page. Persisted as a JSON string on IStorefrontSettings.loginPageConfig.
// Mirrored by the storefront runtime (ac-storefront-web: Components/Helpers/loginPageConfig.ts) —
// keep the two in step.
//
// These four pages share one look on purpose. A shopper moves between them inside a single flow —
// enter email, land on confirm, get signed in — and letting them drift is how a branded sign-in ends
// up handing off to a page that looks like somebody else's product.
//
// Style parts reuse the generic search-config style shape (ISearchElementStyle) and are applied with
// elementStyleFrom: numbers are px, fontSize is a percent, any camelCase CSS passes through, and each
// part accepts a `$styleRef` to a named Storefront Style like every other styled element.
//
// NOT covered: the multi-tenant /login page. With no storefront in the URL there are no storefront
// settings to read, so it has nothing to style from.

import { ISearchElementStyle } from './searchPageConfig';

export interface ILoginPageConfig {
    // ── Content ──
    /**
     * Full-bleed image behind the card. Sized to cover, so a wide brand photo works and a small one is
     * scaled rather than tiled. Layered UNDER `page`'s own background, so a colour set there with any
     * transparency tints the image instead of hiding it.
     */
    backdropImageUrl?: string;
    /** Logo shown above the heading. Absent = no logo, which is today's look. */
    logoUrl?: string;
    /** Replaces the storefront name as the card's heading. Set to '' to drop the heading entirely. */
    headingText?: string;
    /** Optional line under the heading, e.g. "Sign in with your work email". */
    introText?: string;

    // ── Style parts ──
    /** The full-screen backdrop. */
    page?: ISearchElementStyle;
    /** The card the form sits in. */
    box?: ISearchElementStyle;
    /** The logo image (height, max-width, margin). */
    logo?: ISearchElementStyle;
    /** The heading line. */
    heading?: ISearchElementStyle;
    /** The intro line under the heading. */
    intro?: ISearchElementStyle;
    /** Email and code inputs. */
    input?: ISearchElementStyle;
    /** Sign in / Sign up / the code submit. */
    primaryButton?: ISearchElementStyle;
    /** Browse as Guest, sign-up link, "use a different email". */
    secondaryButton?: ISearchElementStyle;
    /** Error and notice bars. */
    message?: ISearchElementStyle;
}

export function parseLoginPageConfig(raw?: string): ILoginPageConfig {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? (parsed as ILoginPageConfig) : {};
    } catch {
        return {};
    }
}

/**
 * The non-style keys: plain strings holding content rather than CSS. Declared so the raw-JSON
 * linter type-checks them instead of warning that they aren't style parts.
 */
export const LOGIN_PAGE_CONTENT_KEYS = ['backdropImageUrl', 'logoUrl', 'headingText', 'introText'] as const;

/** The style parts, in the order the admin panel stacks them. */
export const LOGIN_PAGE_STYLE_PARTS = [
    'page', 'box', 'logo', 'heading', 'intro', 'input', 'primaryButton', 'secondaryButton', 'message',
] as const;
export type LoginPageStylePart = typeof LOGIN_PAGE_STYLE_PARTS[number];

export const LOGIN_PAGE_PART_LABELS: Record<LoginPageStylePart, string> = {
    page: 'Background',
    box: 'Card',
    logo: 'Logo',
    heading: 'Heading',
    intro: 'Intro text',
    input: 'Fields',
    primaryButton: 'Primary button',
    secondaryButton: 'Secondary buttons',
    message: 'Messages',
};

export const LOGIN_PAGE_PART_TOOLTIPS: Record<LoginPageStylePart, string> = {
    page: 'The full-screen backdrop behind the card.',
    box: 'The card the form sits in.',
    logo: 'The logo above the heading (height, width, spacing).',
    heading: 'The store name, or whatever you put in its place.',
    intro: 'The optional line under the heading.',
    input: 'The email and code fields.',
    primaryButton: 'Sign in and Sign up.',
    secondaryButton: 'Browse as Guest, the sign-up link, "use a different email".',
    message: 'Error and notice bars.',
};

/** The style-target keys these parts map to, for the named-Storefront-Style pickers. */
export const LOGIN_PAGE_STYLE_KEY_BY_PART: Record<LoginPageStylePart, string> = {
    page: 'loginPage',
    box: 'loginBox',
    logo: 'loginLogo',
    heading: 'loginHeading',
    intro: 'loginIntro',
    input: 'loginInput',
    primaryButton: 'loginPrimaryButton',
    secondaryButton: 'loginSecondaryButton',
    message: 'loginMessage',
};
